import { Box } from '@mui/material';
import { Grid } from '@mui/system';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CustomContainer } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { useWaterfallContext } from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import theme from 'theme';
import WaterfallTableFormatter from '../WaterfallTableFormatter';

const WATERFALL_TABLE_CONTAINER_CLASS = `${WATERFALL_SLUG}-table-container`;
const WATERFALL_FUND_INVESTMENTS_CONTAINER_CLASS = `${WATERFALL_SLUG}-fund-investments-container`;
const WATERFALL_FIRM_TOTAL_CONTAINER_CLASS = `${WATERFALL_SLUG}-firm-total-container`;

// Waterfall sheets use different parser generics but share the complete spreadsheet contract.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WaterfallSheet = ScalarSpreadsheetConfigDataInterface<any, any, any, any>;

const WaterfallTables = ({
  waterfallSheet,
  fundInvestmentsSheets,
  firmTotalSheet,
}: {
  waterfallSheet: WaterfallSheet;
  fundInvestmentsSheets: WaterfallSheet[];
  firmTotalSheet?: WaterfallSheet;
}) => {
  const { onChange, format, formatDispatch } = useWaterfallContext();

  return (
    <Box id={waterfallSheet.name} className="always-visible waterfall-tables-container" sx={{ mt: theme.spacing(1.5) }}>
      <CustomContainer
        display="inline-flex"
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',
        }}
        wrap="nowrap">
        <Grid className={WATERFALL_TABLE_CONTAINER_CLASS} display="inline-table">
          <WaterfallTableFormatter />
          <ScalarSpreadsheet
            {...waterfallSheet}
            // Pass evaluated data explicitly so grid rebuilds after in-place sheet.evaluate().
            // Class-instance spreads omit these; without them ScalarSpreadsheet stays on GridSkeleton.
            data={waterfallSheet?.data ?? []}
            totalData={waterfallSheet?.totalData ?? []}
            titleData={waterfallSheet?.titleData ?? []}
            showToolbar
            format={format}
            formatDispatch={formatDispatch}
            onChange={onChange}
            selectionSummaryUsesCellCurrencyFormat
            tableTerms={{ tableSlug: 'waterfall-table' }}
            disableVerticalScroll
            sheet={waterfallSheet}
          />
        </Grid>

        {fundInvestmentsSheets
          .filter(sheet => sheet?.data)
          .map(fundInvestmentSheet => (
            // The name always exists and is unique as it includes the fund id
            <Grid
              className={WATERFALL_FUND_INVESTMENTS_CONTAINER_CLASS}
              display="inline-table"
              key={fundInvestmentSheet.name}>
              <ScalarSpreadsheet
                {...fundInvestmentSheet}
                showToolbar
                format={format}
                disableVerticalScroll
                formatDispatch={formatDispatch}
                id={fundInvestmentSheet.name}
                tableTerms={{ tableSlug: 'waterfall-table' }}
                onChange={onChange}
                selectionSummaryUsesCellCurrencyFormat
                data={fundInvestmentSheet?.data ?? []}
                totalData={fundInvestmentSheet?.totalData ?? []}
                titleData={fundInvestmentSheet?.titleData ?? []}
                sheet={fundInvestmentSheet}
              />
            </Grid>
          ))}

        {firmTotalSheet && (
          <Grid className={WATERFALL_FIRM_TOTAL_CONTAINER_CLASS} display="inline-table">
            <ScalarSpreadsheet
              {...firmTotalSheet}
              data={firmTotalSheet?.data ?? []}
              totalData={firmTotalSheet?.totalData ?? []}
              titleData={firmTotalSheet?.titleData ?? []}
              showToolbar
              format={format}
              formatDispatch={formatDispatch}
              onChange={onChange}
              selectionSummaryUsesCellCurrencyFormat
              tableTerms={{ tableSlug: 'waterfall-table' }}
              disableVerticalScroll
              sheet={firmTotalSheet}
            />
          </Grid>
        )}
      </CustomContainer>
    </Box>
  );
};

export default WaterfallTables;
